import { useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Helmet } from 'react-helmet';
import { classNames } from 'utils/helpers';

import Nav from 'components/Nav';
import Footer from 'components/Footer';
import { OnboardingProvider, OnboardingModal } from 'components/onboarding';
// import Player from 'components/player/Player';

// New PlayerProvider from microfrontend package
import { PlayerProvider } from '@chest-music/player';
import '@chest-music/player/dist/index.css';


// Redux dependencies for PlayerProvider
import {
  useLazyGetTrackSourceQuery,
  useUpdateTrackPlayMutation
} from 'store/api';
import {
  reset,
  play,
  next,
  previous,
  playing,
  setContext,
  toggleShuffle
} from 'app/playlist';
import {
  closePlayer,
  openPlayer,
  showPlayer,
  hidePlayer
} from 'app/player';

export default function Root() {
  const { player, playlist, auth } = useSelector(state => state);
  const dispatch = useDispatch();
  
  // Auto-open player when a track is added to playlist (same logic as old Player component)
  useEffect(() => {
    const currentTrack = playlist.playlist && playlist.playlist[0];
    
    if (currentTrack && !player.opened) {
      dispatch(openPlayer());
    }
  }, [playlist.playlist, player.opened, dispatch]);

  // Configure PlayerProvider dependencies
  const playerDependencies = {
    apiHooks: {
      useLazyGetTrackSourceQuery,
      useUpdateTrackPlayMutation
    },
    playlistActions: {
      reset: () => dispatch(reset()),
      play: () => dispatch(play()),
      next: () => dispatch(next()),
      previous: () => dispatch(previous()),
      playing: (data) => dispatch(playing(data)),
      setContext: (data) => dispatch(setContext(data)),
      toggleShuffle: () => dispatch(toggleShuffle())
    },
    playerActions: {
      closePlayer: () => dispatch(closePlayer()),
      openPlayer: () => dispatch(openPlayer()),
      showPlayer: () => dispatch(showPlayer()),
      hidePlayer: () => dispatch(hidePlayer())
    },
    reduxProps: {
      dispatch,
      playlist: playlist.playlist,
      playlistState: playlist,
      playerState: player
    }
  };

  return (
    <OnboardingProvider
      autoShow={!!(auth?.user && (auth?.user?.uid || auth?.user?.email))}
      userId={auth?.user?.uid || null}
      email={auth?.user?.email || null}
    >
      <Helmet>
        <meta property="og:title" content="Chest Music" />
        <meta property="og:description" content="If your music is your treasure, it deserves to have its chest." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://chestmusic.com/" />
        <meta property="og:image" content="https://chestmusic.com/images/og-cover.jpg" />
        <meta property="og:image:type" content="image/jpeg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <script>
          {`
            (function(h,o,t,j,a,r){ 
              h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)}; 
              h._hjSettings={hjid:3868461,hjsv:6}; 
              a=o.getElementsByTagName('head')[0]; 
              r=o.createElement('script');
              r.async=1; 
              r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv; 
              a.appendChild(r); 
            })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');
          `}
        </script>
      </Helmet>
      <div className={classNames({
        'flex flex-col h-full': true
      })}>
        <header>
          <Nav />
        </header>
        <main>
          <Outlet />
        </main>
        <Footer />
        <PlayerProvider 
          apiHooks={playerDependencies.apiHooks}
          playlistActions={playerDependencies.playlistActions}
          playerActions={playerDependencies.playerActions}
          reduxProps={playerDependencies.reduxProps}
        />
      </div>
      <OnboardingModal />
    </OnboardingProvider>
  )
}